// @ts-nocheck
import { Button, Card, Col, Modal, Row, Tag, Space } from 'antd';
import SharedTable from '../Table/Table';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
    CASHOUT_BLOCK_TYPES,
    PERMISSIONS,
    TICKET_CHANGE_TYPES,
    TICKET_STATUSES,
    TICKET_TYPES,
} from '../../../utils/constants';
import {
    blockTicketPayment,
    cancelTicket,
    changeConfirmModalProps,
    changeTicketInfoModalProps,
    onTicketConfirmModalCancel,
    resetConfirmModalProps,
    ticketSettlementRequestConfirm,
} from '../../../redux/actions/global';
import useAccess from '../../../utils/hooks/useAccess';
import React, { useMemo } from 'react';
import { MENUS } from '../../../utils/routes';
import { changeSettlementInfoDrawerProps } from '../../../redux/actions/settlement';
import useAdmin from '../../../utils/hooks/useAdmin';
import convertToOriginalFormat from '../../../utils/methods/ConvertToOriginalFormat';
import { toggleCombinationLockItemByTicket } from '../../../redux/actions/combination-lock';
import Logs from './Logs/Logs';
import PlayerDetailsLink from '../Table/PlayerDetailsLink/PlayerDetailsLink';
import useTicketInformationColumns from './columns/use-ticket-information-columns';

import styles from './TicketIformation.module.scss';
import { api } from '../../../api';
import GeneralService from '../../../services/GeneralService';
import { unblockTicketCashout } from '../../../redux/actions/cashout-block';

const TicketInformation = props => {
    const dispatch = useDispatch();
    const checkAccess = useAccess();
    const checkAdmin = useAdmin();
    const isUserAdmin = checkAdmin(MENUS.ticketModal.id);

    const canUserLockCombinations = checkAccess(MENUS.combinationLocks.id, PERMISSIONS.update);
    const canUserSettleBetLine = checkAccess(MENUS.settlementTickets.id, PERMISSIONS.create);
    const canUserCreate = checkAccess(MENUS.ticketModal.id, PERMISSIONS.create);
    const canUserBlock = checkAccess(MENUS.cashoutBlockEvents.id, PERMISSIONS.create);

    const ticketInfoModalProps = useSelector(state => state.global.ticketInfoModalProps);

    // const { cashier } = props;
    const { isVisible, ticket, unSavedTicket } = ticketInfoModalProps;

    // const { printLimitExceeded, ticketId } = ticket;
    const isLive = useMemo(() => ticket.betLines?.some(betLine => betLine.live), [ticket]);

    const onCombinationLock = () => {
        dispatch(
            changeConfirmModalProps({
                isVisible: true,
                shouldRefresh: false,
                successCallback: async props => {
                    const { info } = await dispatch(toggleCombinationLockItemByTicket(props));
                    return info;
                },
                cancelCallback: () => {
                    dispatch(resetConfirmModalProps());
                },
                title: `${ticket.isLocked ? 'Unlock' : 'Lock'} Combination`,
                successMessage: `Combination ${ticket.isLocked ? 'unlocked' : 'locked'}`,
                failMessage: `${ticket.isLocked ? 'Unlock' : 'Lock'} Failed!`,
            }),
        );
    };

    const onBlockPaymentClick = () => {
        dispatch(
            changeConfirmModalProps({
                isVisible: true,
                shouldRefresh: true,
                successCallback: async props => {
                    const params = {
                        code: ticket.code,
                        block: !ticket.paymentBlock,
                        channel: ticket.channel,
                        ...props,
                    };
                    const { info } = await dispatch(blockTicketPayment(params));
                    return info;
                },
                cancelCallback: () => {
                    dispatch(resetConfirmModalProps());
                },
                title: ticket.paymentBlock ? 'Unblock Payment' : 'Block Payment',
                successMessage: ticket.paymentBlock ? 'Payment Unblocked' : 'Payment Blocked',
                failMessage: 'Block Payment failed!',
            }),
        );
    };

    const onTicketCancelClick = () => {
        dispatch(
            changeConfirmModalProps({
                isVisible: true,
                shouldRefresh: true,
                successCallback: async props => {
                    const { info } = await dispatch(cancelTicket(props));
                    return info;
                },
                cancelCallback: () => {
                    dispatch(onTicketConfirmModalCancel());
                },
                title: 'Cancel Ticket',
                successMessage: 'Ticket cancel successfully completed!',
                failMessage: 'Ticket cancel failed!',
            }),
        );
    };

    const handleApply = () => {
        dispatch(
            changeConfirmModalProps({
                isVisible: true,
                shouldRefresh: true,
                successCallback: async props => {
                    const { info } = await dispatch(ticketSettlementRequestConfirm(props));
                    return info;
                },
                cancelCallback: async () => {
                    await dispatch(onTicketConfirmModalCancel());
                },
                title: 'Settlement request',
                successMessage: 'Settlement successfully completed!',
                failMessage: 'Ticket settlement failed!',
            }),
        );
    };

    const onModalClose = () => {
        dispatch(changeTicketInfoModalProps({ isVisible: false }));
    };

    let ticketSelectionDetails;

    if (isVisible) {
        ticketSelectionDetails = ticket.betLines?.reduce(
            (result, betLine) => ({
                ...result,
                sports: [...result.sports, betLine.sportName],
                tournaments: [...result.tournaments, betLine.league],
                events: [...result.events, betLine.event],
                markets: [...result.markets, betLine.marketName],
            }),
            { sports: [], tournaments: [], events: [], markets: [] },
        );

        ticketSelectionDetails = Object.entries(ticketSelectionDetails).reduce(
            (result, [key, value]) => ({
                ...result,
                [key]: [...new Set(value)],
            }),
            {},
        );
    }

    const isTicketManuallySettled = unSavedTicket.betLines?.some(status => status.isSettlementStatusChanged);

    const handleSelectStatus = (value, id) => {
        dispatch(
            changeTicketInfoModalProps({
                isVisible: true,
                unSavedTicket: {
                    ...unSavedTicket,
                    betLines: unSavedTicket.betLines.map(betLine =>
                        id === betLine.betLineId
                            ? {
                                  ...betLine,
                                  settlementStatus: value,
                                  manuallySettled: true,
                                  isSettlementStatusChanged: true,
                              }
                            : betLine,
                    ),
                },
            }),
        );
    };

    const handleClickRollback = id => {
        dispatch(
            changeTicketInfoModalProps({
                isVisible: true,
                unSavedTicket: {
                    ...unSavedTicket,
                    betLines: unSavedTicket.betLines.map(betLine =>
                        id === betLine.betLineId
                            ? {
                                  ...betLine,
                                  manuallySettled: false,
                                  isSettlementStatusChanged: true,
                              }
                            : betLine,
                    ),
                },
            }),
        );
        dispatch(
            changeConfirmModalProps({
                isVisible: true,
                shouldRefresh: true,
                successCallback: async props => {
                    const { info } = await dispatch(ticketSettlementRequestConfirm(props));
                    return info;
                },
                cancelCallback: async () => {
                    await dispatch(onTicketConfirmModalCancel());
                },
                title: 'Settlement rollback request',
                successMessage: 'Settlement rollback successfully completed!',
                failMessage: 'Ticket settlement rollback failed!',
            }),
        );
    };

    const openSettlementDrawer = logs => {
        dispatch(changeSettlementInfoDrawerProps({ isVisible: true, logs, type: 'ticket' }));
    };

    const onCashoutBlockTicket = () => {
        const isBlocked = ticket.isCashOutBlocked;

        dispatch(
            changeConfirmModalProps({
                isVisible: true,
                title: isBlocked ? 'Unblock Ticket' : 'Block Ticket',
                placeholder: 'Please enter your comment',
                successMessage: isBlocked ? 'Ticket unblocked successfully!' : 'Ticket blocked successfully!',
                failMessage: isBlocked ? 'Ticket unblock failed!' : 'Ticket block failed!',

                successCallback: async ({ commentByUser }) => {
                    let info;

                    if (isBlocked) {
                        const result = await dispatch(
                            unblockTicketCashout({
                                templateId: ticket.betId,
                                comment: commentByUser,
                                operation: CASHOUT_BLOCK_TYPES.TICKET,
                            }),
                        );
                        info = result.info;
                    } else {
                        const payload = {
                            ticketId: ticket.betId,
                            comment: commentByUser,
                            cashOutBlockTypes: CASHOUT_BLOCK_TYPES.TICKET,
                        };
                        const result = await GeneralService.blockCashout(payload);
                        info = result.info;
                    }

                    if (info?.success) {
                        dispatch(
                            changeTicketInfoModalProps({
                                isVisible: true,
                                ticket: {
                                    ...ticket,
                                    isCashOutBlocked: !isBlocked,
                                },
                            }),
                        );
                    }

                    return info;
                },

                cancelCallback: () => dispatch(resetConfirmModalProps()),
            }),
        );
    };

    const { data } = api.onlineBackOffice.useGetTicketCurrentCashout({
        accountNumber: ticket.accountNumber,
        ticketCode: ticket.code,
        enabled: ticket.status === 1,
    });

    const cashoutAmount = data?.cashoutAmount;

    const columns = useTicketInformationColumns({
        isLive,
        handleSelectStatus,
        canUserSettleBetLine,
        handleClickRollback,
        openSettlementDrawer,
    });

    return (
        <Modal
            className={styles.modal}
            visible={isVisible}
            maskClosable={false}
            centered={true}
            closable={true}
            onCancel={onModalClose}
            footer={null}
        >
            {isVisible ? (
                <div>
                    <Card
                        className={`${styles.card} ${styles.betInformationCard}`}
                        title={
                            <div className={'flex justify-between align-center'}>
                                {/*<span>Ticket Information {ticket.code}</span>*/}
                                <div className={'flex align-center'}>
                                    <span className={'m-r-4'}>Ticket Information {isLive && <Tag>Live</Tag>}</span>
                                    <Logs ticketCode={ticket.code} />
                                </div>
                                {canUserCreate && (
                                    <Space>
                                        {canUserBlock && (
                                            <Button type={'danger'} onClick={onCashoutBlockTicket}>
                                                {ticket.isCashOutBlocked ? 'Unblock Cashout' : 'Cashout Block'}
                                            </Button>
                                        )}
                                        {isUserAdmin && (
                                            <Button
                                                type={'danger'}
                                                disabled={ticket.status === TICKET_CHANGE_TYPES.CANCELLED_MANUALLY}
                                                onClick={onTicketCancelClick}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                        {canUserLockCombinations && (
                                            <Button type={'danger'} onClick={onCombinationLock}>
                                                {ticket.isLocked ? 'Unlock Combination' : 'Lock Combination'}
                                            </Button>
                                        )}
                                        {ticket.channel !== 'ONLINE' ? (
                                            <Button type={'danger'} onClick={onBlockPaymentClick}>
                                                {ticket.paymentBlock ? 'Unblock Payment' : 'Block Payment'}
                                            </Button>
                                        ) : null}
                                    </Space>
                                )}
                            </div>
                        }
                        size={'small'}
                    >
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bet ID</Col>
                            <Col span={12}>{ticket.betId}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Ticket Code</Col>
                            <Col span={12}>{ticket.code}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Account Number</Col>
                            <Col span={12}>
                                <PlayerDetailsLink customerId={ticket.accountNumber} />
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Full Name</Col>
                            <Col span={12}>{ticket.issuerAgentFullname}</Col>
                        </Row>{' '}
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bet Request Date</Col>
                            <Col span={12}>{moment(ticket.requestedDate).format('DD/MM/YYYY HH:mm:ss')}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bet Placement Date</Col>
                            <Col span={12}>{moment(ticket.issuedDate).format('DD/MM/YYYY HH:mm:ss')}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bet Settlement Date</Col>
                            <Col span={12}>
                                {ticket.settlementDate
                                    ? moment(ticket.settlementDate).format('DD/MM/YYYY HH:mm:ss')
                                    : '-'}
                            </Col>
                        </Row>
                        {/*<Row className={`${styles.row}`}>*/}
                        {/*    <Col span={12}>Username</Col>*/}
                        {/*    <Col span={12}>{ticket.issuerAgentUsername}</Col>*/}
                        {/*</Row>*/}
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bet Type</Col>
                            <Col span={12}>
                                {ticket.ticketType}{' '}
                                {ticket.ticketType === TICKET_TYPES.system ||
                                ticket.ticketType === TICKET_TYPES.multiSystem
                                    ? ticket.systemValues.trim().split(' ').join(', ')
                                    : ''}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Sport</Col>
                            <Col span={12}>
                                {ticketSelectionDetails.sports.length > 1
                                    ? `${ticketSelectionDetails.sports.length} Types`
                                    : ticketSelectionDetails.sports[0]}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Tournament</Col>
                            <Col span={12}>
                                {ticketSelectionDetails.tournaments.length > 1
                                    ? `${ticketSelectionDetails.tournaments.length} Tournaments`
                                    : ticketSelectionDetails.tournaments[0]}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Event</Col>
                            <Col span={12}>
                                {ticketSelectionDetails.events.length > 1
                                    ? `${ticketSelectionDetails.events.length} Events`
                                    : ticketSelectionDetails.events[0]}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Market</Col>
                            <Col span={12}>
                                {ticketSelectionDetails.markets.length > 1
                                    ? `${ticketSelectionDetails.markets.length} Markets`
                                    : ticketSelectionDetails.markets[0]}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Selection</Col>
                            <Col span={12}>
                                {ticket.betLines.length > 1
                                    ? `${ticket.betLines.length} Selections`
                                    : ticket.betLines[0].outcomeName}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Odds</Col>
                            <Col span={12}>{ticket.odds}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Stake</Col>
                            <Col span={12}>{convertToOriginalFormat(ticket.totalStakeAmount)} AZN</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Free Bet</Col>
                            <Col span={12}>{convertToOriginalFormat(ticket.freeBetAmount)} AZN</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Possible Win</Col>
                            <Col span={12}>{convertToOriginalFormat(ticket.totalWinAmount)} AZN</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bonus</Col>
                            <Col span={12}>{convertToOriginalFormat(ticket.totalBonusAmount)} AZN</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Win Tax</Col>
                            <Col span={12}>{convertToOriginalFormat(ticket.winTax)} AZN</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>To Win</Col>
                            <Col span={12}>{convertToOriginalFormat(ticket.totalPayAmount)} AZN</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Bet Status</Col>
                            <Col span={12}>{TICKET_STATUSES[ticket.status]}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Paid by</Col>
                            <Col span={12}>{ticket.paidAt > 0 ? ticket.paidBy : '-'}</Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Paid Date</Col>
                            <Col span={12}>
                                {ticket.paidAt > 0 ? moment(ticket.paidAt).format('DD/MM/YYYY HH:mm:ss') : '-'}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Paid Amount</Col>
                            <Col span={12}>
                                {ticket.payoutAmount > 0 ? convertToOriginalFormat(ticket.payoutAmount) + ' AZN' : '-'}
                            </Col>
                        </Row>
                        <Row className={`${styles.row}`}>
                            <Col span={12}>Cashout Amount</Col>
                            <Col span={12}>{cashoutAmount !== undefined ? cashoutAmount + ' AZN' : '-'}</Col>
                        </Row>
                    </Card>
                    <Card className={`${styles.card} ${styles.selectionsCard}`} title='Selections' size={'small'}>
                        <SharedTable data={ticket.betLines} rowKey={record => record.id} columns={columns} />
                    </Card>
                    {isTicketManuallySettled && (
                        <div className={`${styles.applyButton}`}>
                            <Button type={'primary'} onClick={() => handleApply()}>
                                Apply
                            </Button>
                        </div>
                    )}
                </div>
            ) : null}
        </Modal>
    );
};

export default TicketInformation;
